export function PlanOfCareScanAnimation() {
    return (
      <div aria-hidden className="w-full max-w-[420px]">
        <style>
          {`
            @keyframes poc-scan-y {
              0% { transform: translateY(-35%); opacity: 0; }
              10% { opacity: 1; }
              90% { opacity: 1; }
              100% { transform: translateY(270%); opacity: 0; }
            }
            @keyframes poc-shimmer-x {
              0% { transform: translateX(-120%); opacity: 0; }
              15% { opacity: 1; }
              100% { transform: translateX(120%); opacity: 0; }
            }
            @keyframes poc-float {
              0% { transform: translateY(0px) rotate(-1deg); }
              50% { transform: translateY(6px) rotate(-1deg); }
              100% { transform: translateY(0px) rotate(-1deg); }
            }
            @media (prefers-reduced-motion: reduce) {
              .poc-scanBeam,
              .poc-scanLine,
              .poc-shimmer::after,
              .poc-topBar,
              .poc-float {
                animation: none !important;
              }
            }
          `}
        </style>
  
        <div className="relative rounded-2xl border border-border bg-muted/20 p-6 overflow-hidden">
          {/* ambient glow */}
          <div className="pointer-events-none absolute -left-20 -bottom-24 size-64 rounded-full bg-blue-500/5 dark:bg-blue-400/10 blur-3xl" />
          <div className="pointer-events-none absolute -right-24 -top-24 size-72 rounded-full bg-indigo-500/5 dark:bg-indigo-400/10 blur-3xl" />
  
          {/* paper */}
          <div
            className="poc-float relative mx-auto mt-2 aspect-[3/4] w-full max-w-[340px] overflow-hidden rounded-xl border border-border bg-card shadow-[0_28px_70px_-50px_rgba(0,0,0,0.65)]"
            style={{ animation: "poc-float 3.4s ease-in-out infinite" }}
          >
            <div className="px-6 pt-6 pb-7">
              <div className="text-xs font-medium tracking-wide text-foreground/80">
                plan-of-care.pdf
              </div>
  
              {/* Skeleton for the plan of care text */}
              <div className="mt-6 space-y-2.5">
                <div className="poc-shimmer h-2.5 w-[74%] rounded-full bg-muted/60 relative overflow-hidden" />
                <div className="poc-shimmer h-2.5 w-[92%] rounded-full bg-muted/60 relative overflow-hidden" />
                <div className="poc-shimmer h-2.5 w-[84%] rounded-full bg-muted/60 relative overflow-hidden" />
                <div className="poc-shimmer h-2.5 w-[66%] rounded-full bg-muted/60 relative overflow-hidden" />
  
                <div className="h-5" />
  
                <div className="poc-shimmer h-2.5 w-[58%] rounded-full bg-muted/60 relative overflow-hidden" />
                <div className="poc-shimmer h-2.5 w-[95%] rounded-full bg-muted/60 relative overflow-hidden" />
                <div className="poc-shimmer h-2.5 w-[90%] rounded-full bg-muted/60 relative overflow-hidden" />
  
                <div className="h-5" />
  
                <div className="poc-shimmer h-2.5 w-[40%] rounded-full bg-muted/60 relative overflow-hidden" />
                <div className="poc-shimmer h-2.5 w-[82%] rounded-full bg-muted/60 relative overflow-hidden" />
                <div className="poc-shimmer h-2.5 w-[88%] rounded-full bg-muted/60 relative overflow-hidden" />
                <div className="poc-shimmer h-2.5 w-[70%] rounded-full bg-muted/60 relative overflow-hidden" />
  
                <div className="h-5" />
  
                <div className="poc-shimmer h-2.5 w-[60%] rounded-full bg-muted/60 relative overflow-hidden" />
                <div className="poc-shimmer h-2.5 w-[45%] rounded-full bg-muted/60 relative overflow-hidden" />
                <div className="poc-shimmer h-2.5 w-[52%] rounded-full bg-muted/60 relative overflow-hidden" />
              </div>
            </div>
  
            {/* scanning overlay */}
            <div className="pointer-events-none absolute inset-0">
              <div
                className="poc-scanBeam absolute -left-[8%] -right-[8%] top-0 h-28"
                style={{
                  background:
                    "linear-gradient(180deg, transparent, hsl(var(--primary) / 0.22), transparent)",
                  filter: "blur(1px)",
                  animation: "poc-scan-y 2.4s ease-in-out infinite"
                }}
              />
            </div>
  
            {/* shimmer overlay for lines */}
            <style>
              {`
                .poc-shimmer::after {
                  content: "";
                  position: absolute;
                  inset: 0;
                  transform: translateX(-120%);
                  background: linear-gradient(
                    90deg,
                    transparent,
                    hsl(var(--foreground) / 0.10),
                    transparent
                  );
                  animation: poc-shimmer-x 2.2s ease-in-out infinite;
                }
                .poc-shimmer:nth-child(2n)::after { animation-delay: .25s; }
                .poc-shimmer:nth-child(3n)::after { animation-delay: .5s; }
              `}
            </style>
          </div>
        </div>
      </div>
    );
}

export type ProviderAvailabilitySearchAnimationProps = {
  /**
   * Labels for the current search window, typically 4 weeks.
   * Example: ["Dec 15–21", "Dec 22–28", "Dec 29–Jan 4", "Jan 5–11"]
   */
  weeks: string[];
  /**
   * Optional batch label shown above the week chips.
   * Example: "Searching next 4 weeks" or "Weeks 1–4"
   */
  batchLabel?: string;
};

export function ProviderAvailabilitySearchAnimation({
  weeks,
  batchLabel = "Searching 4-week window"
}: ProviderAvailabilitySearchAnimationProps) {
  const visibleWeeks = weeks?.length ? weeks : ["Week 1", "Week 2", "Week 3", "Week 4"];
  const rangeLabel =
    visibleWeeks.length >= 2
      ? `${visibleWeeks[0]} → ${visibleWeeks[visibleWeeks.length - 1]}`
      : visibleWeeks[0];

  return (
    <div aria-hidden className="w-full max-w-[520px]">
      <style>
        {`
          @keyframes poc-search-bar {
            0% { background-position: 0% 50%; opacity: .55; }
            50% { background-position: 100% 50%; opacity: .95; }
            100% { background-position: 0% 50%; opacity: .55; }
          }
          @keyframes poc-week-pulse {
            0% { opacity: 0; transform: scaleX(.96); }
            12% { opacity: 1; transform: scaleX(1); }
            60% { opacity: .25; transform: scaleX(1); }
            100% { opacity: 0; transform: scaleX(.98); }
          }
          @keyframes poc-dot-travel {
            0% { transform: translateX(0%); opacity: .35; }
            10% { opacity: 1; }
            50% { opacity: 1; }
            90% { opacity: 1; }
            100% { transform: translateX(100%); opacity: .35; }
          }
          @media (prefers-reduced-motion: reduce) {
            .poc-searchTop,
            .poc-weekPulse,
            .poc-dot {
              animation: none !important;
            }
          }
        `}
      </style>

      <div className="relative overflow-hidden rounded-2xl border border-border bg-muted/20 p-5">
        {/* ambient glow */}
        <div className="pointer-events-none absolute -left-20 -bottom-24 size-64 rounded-full bg-blue-500/5 dark:bg-blue-400/10 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 -top-24 size-72 rounded-full bg-indigo-500/5 dark:bg-indigo-400/10 blur-3xl" />

        <div className="relative">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-xs font-medium tracking-wide text-foreground/80">
                {batchLabel}
              </div>
              <div className="mt-1 text-[11px] text-muted-foreground">
                {rangeLabel}
              </div>
            </div>

            {/* animated "search" bar */}
            <div
              className="poc-searchTop h-[10px] w-24 rounded-full"
              style={{
                background:
                  "linear-gradient(90deg, transparent, hsl(var(--primary) / 0.65), transparent)",
                backgroundSize: "200% 100%",
                animation: "poc-search-bar 1.8s ease-in-out infinite"
              }}
            />
          </div>

          {/* timeline rail */}
          <div className="mt-5">
            <div className="relative h-1.5 rounded-full bg-muted/60 overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 w-full"
                style={{
                  background:
                    "linear-gradient(90deg, hsl(var(--primary) / 0.0), hsl(var(--primary) / 0.35), hsl(var(--primary) / 0.0))",
                  filter: "blur(.2px)",
                  opacity: 0.7
                }}
              />
              <div
                className="poc-dot absolute top-1/2 size-2 -translate-y-1/2 rounded-full"
                style={{
                  left: 0,
                  background: "hsl(var(--primary) / 0.95)",
                  boxShadow: "0 0 18px hsl(var(--primary) / 0.35)",
                  animation: "poc-dot-travel 2.6s ease-in-out infinite"
                }}
              />
            </div>
          </div>

          {/* week chips */}
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            {visibleWeeks.slice(0, 8).map((label, idx) => (
              <div
                key={`${label}-${idx}`}
                className="relative overflow-hidden rounded-xl border border-border bg-card px-3 py-2"
              >
                <div className="text-[11px] font-medium text-foreground/85 truncate">
                  {label}
                </div>

                <div className="mt-1 h-1.5 w-full rounded-full bg-muted/60 overflow-hidden">
                  <div
                    className="poc-weekPulse h-full w-full"
                    style={{
                      transformOrigin: "left center",
                      background:
                        "linear-gradient(90deg, hsl(var(--primary) / 0.10), hsl(var(--primary) / 0.55), hsl(var(--primary) / 0.10))",
                      animation: "poc-week-pulse 2.6s ease-in-out infinite",
                      animationDelay: `${idx * 0.22}s`
                    }}
                  />
                </div>

                {/* subtle scanning sheen */}
                <div
                  className="pointer-events-none absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(120deg, transparent 0%, hsl(var(--foreground) / 0.05) 38%, transparent 68%)",
                    opacity: 0.35
                  }}
                />
              </div>
            ))}
          </div>

          <div className="mt-4 flex items-center justify-between text-[11px] text-muted-foreground">
            <span>Checking calendars &amp; rules</span>
            <span className="tabular-nums">4-week batches</span>
          </div>
        </div>
      </div>
    </div>
  );
}